import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import BackButton from '../components/BackButton';
import { db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import PortalBackground from '../components/PortalBackground';

const ProposalAnalysis = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState('Lite');

  useEffect(() => {
    if (!leadId) {
      setError('Lead ID is required');
      setLoading(false);
      return;
    }

    // Set up real-time listener for lead data
    const leadDocRef = doc(db, 'accounts', leadId);
    const unsubscribe = onSnapshot(
      leadDocRef,
      (docSnapshot) => {
        try {
          if (!docSnapshot.exists()) {
            setError('Lead not found');
            setLoading(false);
            return;
          }
          
          const data = docSnapshot.data();
          setLead({ id: docSnapshot.id, ...data });
          setLoading(false);
          setError(null);
        } catch (err) {
          console.error('Error loading lead data:', err);
          setError(err.message || 'Failed to load lead data');
          setLoading(false);
        }
      },
      (err) => {
        console.error('Error in onSnapshot:', err);
        setError('Failed to load lead data');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [leadId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center relative">
        <PortalBackground />
        <div className="relative text-xl text-gray-600 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center relative">
        <PortalBackground />
        <div className="relative text-xl text-red-600">{error}</div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="min-h-screen flex items-center justify-center relative">
        <PortalBackground />
        <div className="relative text-xl text-gray-600 dark:text-gray-400">Lead not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-6">
          <BackButton />
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white flex-1 text-center">
            Program Overview
          </h1>
          <div className="w-20"></div> {/* Spacer for centering */}
        </div>
        
        {/* Program Selector */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-4 mb-6">
          <div className="flex justify-center gap-2">
            {['Lite', 'Plus', 'Pro', 'BBA Bundle'].map((program) => (
              <button
                key={program}
                onClick={() => setSelectedProgram(program)}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                  selectedProgram === program
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {program}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">

          {/* Program-specific content will go here */}
          {selectedProgram === 'Lite' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">Lite Program</h2>

              {/* Calendar Section */}
              <div className="bg-gray-50 dark:bg-gray-800/60 rounded-xl p-6 mt-8 relative">
                {/* Time Investment - Small corner box */}
                {(() => {
                  const getTimeInvestment = (program) => {
                    switch (program) {
                      case 'Lite': return { min: 2, max: 3 };
                      case 'Plus': return { min: 2, max: 4 };
                      case 'Pro': return { min: 3, max: 6 };
                      case 'BBA Bundle': return { min: 5, max: 10 };
                      default: return { min: 2, max: 3 };
                    }
                  };
                  const hours = getTimeInvestment(selectedProgram);
                  return (
                    <div className="absolute top-4 right-4 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-2 border border-gray-200 dark:border-gray-600">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Time Investment</p>
                      <p className="text-xs font-semibold text-gray-900 dark:text-white">
                        {hours.min}-{hours.max} hrs/week
                      </p>
                    </div>
                  );
                })()}
                
                {(() => {
                  // Get next month and year
                  const now = new Date();
                  const nextMonth = now.getMonth() + 1;
                  const nextMonthYear = nextMonth === 12 ? now.getFullYear() + 1 : now.getFullYear();
                  const actualNextMonth = nextMonth === 12 ? 0 : nextMonth;
                  
                  // Get first day of month and number of days
                  const firstDay = new Date(nextMonthYear, actualNextMonth, 1);
                  const lastDay = new Date(nextMonthYear, actualNextMonth + 1, 0);
                  const daysInMonth = lastDay.getDate();
                  const startingDayOfWeek = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.
                  
                  // Day names
                  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                  
                  // Generate calendar days
                  const calendarDays = [];
                  
                  // Find first Monday and third Monday of the month
                  let firstMonday = null;
                  let thirdMonday = null;
                  let mondayCount = 0;
                  
                  for (let day = 1; day <= daysInMonth; day++) {
                    const date = new Date(nextMonthYear, actualNextMonth, day);
                    const dayOfWeek = date.getDay();
                    if (dayOfWeek === 1) { // Monday = 1
                      mondayCount++;
                      if (mondayCount === 1) {
                        firstMonday = day;
                      } else if (mondayCount === 3) {
                        thirdMonday = day;
                      }
                    }
                  }
                  
                  // Add empty cells for days before the first day of the month
                  for (let i = 0; i < startingDayOfWeek; i++) {
                    calendarDays.push(null);
                  }
                  
                  // Add all days of the month
                  for (let day = 1; day <= daysInMonth; day++) {
                    const date = new Date(nextMonthYear, actualNextMonth, day);
                    const dayOfWeek = date.getDay();
                    // Tuesday = 2, Thursday = 4
                    const hasBreakthroughCall = dayOfWeek === 2 || dayOfWeek === 4;
                    const isFinancialReview = day === firstMonday;
                    const isStrategyCall = day === thirdMonday;
                    calendarDays.push({ day, hasBreakthroughCall, isFinancialReview, isStrategyCall });
                  }
                  
                  return (
                    <div className="max-w-4xl mx-auto">
                      <div className="text-center mb-4">
                        <h4 className="text-xl font-semibold text-gray-900 dark:text-white">
                          {monthNames[actualNextMonth]} {nextMonthYear}
                        </h4>
                      </div>
                      
                      {/* Day headers */}
                      <div className="grid grid-cols-7 gap-1 mb-2">
                        {dayNames.map((dayName) => (
                          <div
                            key={dayName}
                            className="text-center text-sm font-semibold text-gray-700 dark:text-gray-300 py-2"
                          >
                            {dayName}
                          </div>
                        ))}
                      </div>
                      
                      {/* Calendar grid */}
                      <div className="grid grid-cols-7 gap-1">
                        {calendarDays.map((dayData, index) => {
                          if (dayData === null) {
                            return (
                              <div
                                key={`empty-${index}`}
                                className="aspect-square bg-transparent"
                              />
                            );
                          }
                          
                          // Determine background color based on events
                          let bgColor = 'bg-white dark:bg-gray-800';
                          if (dayData.hasBreakthroughCall) {
                            bgColor = 'bg-green-50 dark:bg-green-900/30';
                          } else if (dayData.isFinancialReview || dayData.isStrategyCall) {
                            bgColor = 'bg-blue-50 dark:bg-blue-900/30';
                          }
                          
                          return (
                            <div
                              key={dayData.day}
                              className={`aspect-square border border-gray-300 dark:border-gray-600 rounded-lg p-2 flex flex-col items-center justify-start ${bgColor}`}
                            >
                              <div className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                                {dayData.day}
                              </div>
                              {dayData.isFinancialReview && (
                                <div className="w-full mb-1">
                                  <div className="bg-blue-500 text-white text-xs px-1 py-0.5 rounded text-center font-medium">
                                    Financial Review
                                  </div>
                                </div>
                              )}
                              {dayData.isStrategyCall && (
                                <div className="w-full mb-1">
                                  <div className="bg-blue-500 text-white text-xs px-1 py-0.5 rounded text-center font-medium">
                                    Strategy Call
                                  </div>
                                </div>
                              )}
                              {dayData.hasBreakthroughCall && (
                                <div className="w-full">
                                  <div className="bg-green-500 text-white text-xs px-1 py-0.5 rounded text-center font-medium">
                                    Breakthrough Call
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {selectedProgram === 'Plus' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">Plus Program</h2>
              
              {/* Calendar Section */}
              <div className="bg-gray-50 dark:bg-gray-800/60 rounded-xl p-6 mt-8 relative">
                {/* Time Investment - Small corner box */}
                {(() => {
                  const getTimeInvestment = (program) => {
                    switch (program) {
                      case 'Lite': return { min: 2, max: 3 };
                      case 'Plus': return { min: 2, max: 4 };
                      case 'Pro': return { min: 3, max: 6 };
                      case 'BBA Bundle': return { min: 5, max: 10 };
                      default: return { min: 2, max: 3 };
                    }
                  };
                  const hours = getTimeInvestment(selectedProgram);
                  return (
                    <div className="absolute top-4 right-4 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-2 border border-gray-200 dark:border-gray-600">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Time Investment</p>
                      <p className="text-xs font-semibold text-gray-900 dark:text-white">
                        {hours.min}-{hours.max} hrs/week
                      </p>
                    </div>
                  );
                })()}
                
                {(() => {
                  // Get next month and year
                  const now = new Date();
                  const nextMonth = now.getMonth() + 1;
                  const nextMonthYear = nextMonth === 12 ? now.getFullYear() + 1 : now.getFullYear();
                  const actualNextMonth = nextMonth === 12 ? 0 : nextMonth;
                  
                  const firstDay = new Date(nextMonthYear, actualNextMonth, 1);
                  const lastDay = new Date(nextMonthYear, actualNextMonth + 1, 0);
                  const daysInMonth = lastDay.getDate();
                  const startingDayOfWeek = firstDay.getDay();
                  
                  // Find first Monday, second Monday, last Monday, and third Wednesday of the month
                  let firstMonday = null;
                  let secondMonday = null;
                  let lastMonday = null;
                  let thirdWednesday = null;
                  let mondayCount = 0;
                  let wednesdayCount = 0;
                  
                  for (let day = 1; day <= daysInMonth; day++) {
                    const date = new Date(nextMonthYear, actualNextMonth, day);
                    const dayOfWeek = date.getDay();
                    if (dayOfWeek === 1) { // Monday = 1
                      mondayCount++;
                      if (mondayCount === 1) {
                        firstMonday = day;
                      } else if (mondayCount === 2) {
                        secondMonday = day;
                      }
                      lastMonday = day; // Keep updating to get the last one
                    } else if (dayOfWeek === 3) { // Wednesday = 3
                      wednesdayCount++;
                      if (wednesdayCount === 3) {
                        thirdWednesday = day;
                      }
                    }
                  }
                  
                  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                  const calendarDays = [];
                  for (let i = 0; i < startingDayOfWeek; i++) {
                    calendarDays.push(null);
                  }
                  for (let day = 1; day <= daysInMonth; day++) {
                    const date = new Date(nextMonthYear, actualNextMonth, day);
                    const dayOfWeek = date.getDay();
                    const hasBreakthroughCall = dayOfWeek === 2 || dayOfWeek === 4;
                    const isFinancialReview = day === firstMonday;
                    const isStrategyCall = day === secondMonday || day === lastMonday;
                    const isAutomationsCall = day === thirdWednesday;
                    calendarDays.push({ day, hasBreakthroughCall, isFinancialReview, isStrategyCall, isAutomationsCall });
                  }
                  return (
                    <div className="max-w-4xl mx-auto">
                      <div className="text-center mb-4">
                        <h4 className="text-xl font-semibold text-gray-900 dark:text-white">
                          {monthNames[actualNextMonth]} {nextMonthYear}
                        </h4>
                      </div>
                      <div className="grid grid-cols-7 gap-1 mb-2">
                        {dayNames.map((dayName) => (
                          <div key={dayName} className="text-center text-sm font-semibold text-gray-700 dark:text-gray-300 py-2">
                            {dayName}
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-7 gap-1">
                        {calendarDays.map((dayData, index) => {
                          if (dayData === null) {
                            return <div key={`empty-${index}`} className="aspect-square bg-transparent" />;
                          }
                          
                          // Determine background color based on events
                          let bgColor = 'bg-white dark:bg-gray-800';
                          if (dayData.hasBreakthroughCall) {
                            bgColor = 'bg-green-50 dark:bg-green-900/30';
                          } else if (dayData.isFinancialReview || dayData.isStrategyCall || dayData.isAutomationsCall) {
                            bgColor = 'bg-blue-50 dark:bg-blue-900/30';
                          }
                          
                          return (
                            <div
                              key={dayData.day}
                              className={`aspect-square border border-gray-300 dark:border-gray-600 rounded-lg p-2 flex flex-col items-center justify-start ${bgColor}`}
                            >
                              <div className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                                {dayData.day}
                              </div>
                              {dayData.isFinancialReview && (
                                <div className="w-full mb-1">
                                  <div className="bg-blue-500 text-white text-xs px-1 py-0.5 rounded text-center font-medium">
                                    Financial Review
                                  </div>
                                </div>
                              )}
                              {dayData.isStrategyCall && (
                                <div className="w-full mb-1">
                                  <div className="bg-blue-500 text-white text-xs px-1 py-0.5 rounded text-center font-medium">
                                    Strategy Call
                                  </div>
                                </div>
                              )}
                              {dayData.isAutomationsCall && (
                                <div className="w-full mb-1">
                                  <div className="bg-blue-500 text-white text-xs px-1 py-0.5 rounded text-center font-medium">
                                    Automations Call
                                  </div>
                                </div>
                              )}
                              {dayData.hasBreakthroughCall && (
                                <div className="w-full">
                                  <div className="bg-green-500 text-white text-xs px-1 py-0.5 rounded text-center font-medium">
                                    Breakthrough Call
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {selectedProgram === 'Pro' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">Pro Program</h2>
              
              {/* Calendar Section */}
              <div className="bg-gray-50 dark:bg-gray-800/60 rounded-xl p-6 mt-8 relative">
                {/* Time Investment - Small corner box */}
                {(() => {
                  const getTimeInvestment = (program) => {
                    switch (program) {
                      case 'Lite': return { min: 2, max: 3 };
                      case 'Plus': return { min: 2, max: 4 };
                      case 'Pro': return { min: 3, max: 6 };
                      case 'BBA Bundle': return { min: 5, max: 10 };
                      default: return { min: 2, max: 3 };
                    }
                  };
                  const hours = getTimeInvestment(selectedProgram);
                  return (
                    <div className="absolute top-4 right-4 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-2 border border-gray-200 dark:border-gray-600">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Time Investment</p>
                      <p className="text-xs font-semibold text-gray-900 dark:text-white">
                        {hours.min}-{hours.max} hrs/week
                      </p>
                    </div>
                  );
                })()}
                
                {(() => {
                  // Get next month and year
                  const now = new Date();
                  const nextMonth = now.getMonth() + 1;
                  const nextMonthYear = nextMonth === 12 ? now.getFullYear() + 1 : now.getFullYear();
                  const actualNextMonth = nextMonth === 12 ? 0 : nextMonth;
                  
                  const firstDay = new Date(nextMonthYear, actualNextMonth, 1);
                  const lastDay = new Date(nextMonthYear, actualNextMonth + 1, 0);
                  const daysInMonth = lastDay.getDate();
                  const startingDayOfWeek = firstDay.getDay();
                  
                  // Find various days for Pro program events
                  let firstMonday = null;
                  let thirdMonday = null;
                  let firstWednesday = null;
                  let secondThursday = null;
                  let secondFriday = null;
                  let thirdFriday = null;
                  let thirdWednesday = null;
                  let mondayCount = 0;
                  let wednesdayCount = 0;
                  let thursdayCount = 0;
                  let fridayCount = 0;
                  
                  for (let day = 1; day <= daysInMonth; day++) {
                    const date = new Date(nextMonthYear, actualNextMonth, day);
                    const dayOfWeek = date.getDay();
                    if (dayOfWeek === 1) { // Monday = 1
                      mondayCount++;
                      if (mondayCount === 1) {
                        firstMonday = day;
                      } else if (mondayCount === 3) {
                        thirdMonday = day;
                      }
                    } else if (dayOfWeek === 3) { // Wednesday = 3
                      wednesdayCount++;
                      if (wednesdayCount === 1) {
                        firstWednesday = day;
                      } else if (wednesdayCount === 3) {
                        thirdWednesday = day;
                      }
                    } else if (dayOfWeek === 4) { // Thursday = 4
                      thursdayCount++;
                      if (thursdayCount === 2) {
                        secondThursday = day;
                      }
                    } else if (dayOfWeek === 5) { // Friday = 5
                      fridayCount++;
                      if (fridayCount === 2) {
                        secondFriday = day;
                      } else if (fridayCount === 3) {
                        thirdFriday = day;
                      }
                    }
                  }
                  
                  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                  const calendarDays = [];
                  for (let i = 0; i < startingDayOfWeek; i++) {
                    calendarDays.push(null);
                  }
                  for (let day = 1; day <= daysInMonth; day++) {
                    const date = new Date(nextMonthYear, actualNextMonth, day);
                    const dayOfWeek = date.getDay();
                    const hasBreakthroughCall = dayOfWeek === 2 || dayOfWeek === 4;
                    const isFinancialReview = day === firstMonday || day === thirdMonday;
                    const isStrategyCall = day === firstWednesday || day === thirdFriday;
                    const isCQCVisit = day === secondThursday || day === secondFriday;
                    const isAutomationsCall = day === thirdWednesday;
                    const isWebsite = day === firstWednesday;
                    calendarDays.push({ day, hasBreakthroughCall, isFinancialReview, isStrategyCall, isCQCVisit, isAutomationsCall, isWebsite });
                  }
                  return (
                    <div className="max-w-4xl mx-auto">
                      <div className="text-center mb-4">
                        <h4 className="text-xl font-semibold text-gray-900 dark:text-white">
                          {monthNames[actualNextMonth]} {nextMonthYear}
                        </h4>
                      </div>
                      <div className="grid grid-cols-7 gap-1 mb-2">
                        {dayNames.map((dayName) => (
                          <div key={dayName} className="text-center text-sm font-semibold text-gray-700 dark:text-gray-300 py-2">
                            {dayName}
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-7 gap-1">
                        {calendarDays.map((dayData, index) => {
                          if (dayData === null) {
                            return <div key={`empty-${index}`} className="aspect-square bg-transparent" />;
                          }
                          
                          // Determine background color based on events
                          let bgColor = 'bg-white dark:bg-gray-800';
                          if (dayData.hasBreakthroughCall) {
                            bgColor = 'bg-green-50 dark:bg-green-900/30';
                          } else if (dayData.isFinancialReview || dayData.isStrategyCall || dayData.isCQCVisit || dayData.isAutomationsCall || dayData.isWebsite) {
                            bgColor = 'bg-blue-50 dark:bg-blue-900/30';
                          }
                          
                          return (
                            <div
                              key={dayData.day}
                              className={`aspect-square border border-gray-300 dark:border-gray-600 rounded-lg p-2 flex flex-col items-center justify-start ${bgColor}`}
                            >
                              <div className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                                {dayData.day}
                              </div>
                              {dayData.isFinancialReview && (
                                <div className="w-full mb-1">
                                  <div className="bg-blue-500 text-white text-xs px-1 py-0.5 rounded text-center font-medium">
                                    Financial Review
                                  </div>
                                </div>
                              )}
                              {dayData.isStrategyCall && (
                                <div className="w-full mb-1">
                                  <div className="bg-blue-500 text-white text-xs px-1 py-0.5 rounded text-center font-medium">
                                    Strategy Call
                                  </div>
                                </div>
                              )}
                              {dayData.isCQCVisit && (
                                <div className="w-full mb-1">
                                  <div className="bg-blue-500 text-white text-xs px-1 py-0.5 rounded text-center font-medium">
                                    CQC Visit
                                  </div>
                                </div>
                              )}
                              {dayData.isAutomationsCall && (
                                <div className="w-full mb-1">
                                  <div className="bg-blue-500 text-white text-xs px-1 py-0.5 rounded text-center font-medium">
                                    Automations Call
                                  </div>
                                </div>
                              )}
                              {dayData.isWebsite && (
                                <div className="w-full mb-1">
                                  <div className="bg-blue-500 text-white text-xs px-1 py-0.5 rounded text-center font-medium">
                                    Website
                                  </div>
                                </div>
                              )}
                              {dayData.hasBreakthroughCall && (
                                <div className="w-full">
                                  <div className="bg-green-500 text-white text-xs px-1 py-0.5 rounded text-center font-medium">
                                    Breakthrough Call
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {selectedProgram === 'BBA Bundle' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">BBA Bundle</h2>
              
              {/* Calendar Section */}
              <div className="bg-gray-50 dark:bg-gray-800/60 rounded-xl p-6 mt-8 relative">
                {/* Time Investment - Small corner box */}
                {(() => {
                  const getTimeInvestment = (program) => {
                    switch (program) {
                      case 'Lite': return { min: 2, max: 3 };
                      case 'Plus': return { min: 2, max: 4 };
                      case 'Pro': return { min: 3, max: 6 };
                      case 'BBA Bundle': return { min: 5, max: 10 };
                      default: return { min: 2, max: 3 };
                    }
                  };
                  const hours = getTimeInvestment(selectedProgram);
                  return (
                    <div className="absolute top-4 right-4 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-2 border border-gray-200 dark:border-gray-600">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Time Investment</p>
                      <p className="text-xs font-semibold text-gray-900 dark:text-white">
                        {hours.min}-{hours.max} hrs/week
                      </p>
                    </div>
                  );
                })()}
                
                {(() => {
                  // Get next month and year
                  const now = new Date();
                  const nextMonth = now.getMonth() + 1;
                  const nextMonthYear = nextMonth === 12 ? now.getFullYear() + 1 : now.getFullYear();
                  const actualNextMonth = nextMonth === 12 ? 0 : nextMonth;
                  
                  const firstDay = new Date(nextMonthYear, actualNextMonth, 1);
                  const lastDay = new Date(nextMonthYear, actualNextMonth + 1, 0);
                  const daysInMonth = lastDay.getDate();
                  const startingDayOfWeek = firstDay.getDay();
                  
                  // Find first Monday and third Monday of the month
                  let firstMonday = null;
                  let thirdMonday = null;
                  let mondayCount = 0;
                  
                  for (let day = 1; day <= daysInMonth; day++) {
                    const date = new Date(nextMonthYear, actualNextMonth, day);
                    const dayOfWeek = date.getDay();
                    if (dayOfWeek === 1) { // Monday = 1
                      mondayCount++;
                      if (mondayCount === 1) {
                        firstMonday = day;
                      } else if (mondayCount === 3) {
                        thirdMonday = day;
                      }
                    }
                  }
                  
                  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                  const calendarDays = [];
                  for (let i = 0; i < startingDayOfWeek; i++) {
                    calendarDays.push(null);
                  }
                  for (let day = 1; day <= daysInMonth; day++) {
                    const date = new Date(nextMonthYear, actualNextMonth, day);
                    const dayOfWeek = date.getDay();
                    const hasBreakthroughCall = dayOfWeek === 2 || dayOfWeek === 4;
                    const isFinancialReview = day === firstMonday;
                    const isStrategyCall = day === thirdMonday;
                    calendarDays.push({ day, hasBreakthroughCall, isFinancialReview, isStrategyCall });
                  }
                  return (
                    <div className="max-w-4xl mx-auto">
                      <div className="text-center mb-4">
                        <h4 className="text-xl font-semibold text-gray-900 dark:text-white">
                          {monthNames[actualNextMonth]} {nextMonthYear}
                        </h4>
                      </div>
                      <div className="grid grid-cols-7 gap-1 mb-2">
                        {dayNames.map((dayName) => (
                          <div key={dayName} className="text-center text-sm font-semibold text-gray-700 dark:text-gray-300 py-2">
                            {dayName}
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-7 gap-1">
                        {calendarDays.map((dayData, index) => {
                          if (dayData === null) {
                            return <div key={`empty-${index}`} className="aspect-square bg-transparent" />;
                          }
                          
                          // Determine background color based on events
                          let bgColor = 'bg-white dark:bg-gray-800';
                          if (dayData.hasBreakthroughCall) {
                            bgColor = 'bg-green-50 dark:bg-green-900/30';
                          } else if (dayData.isFinancialReview || dayData.isStrategyCall) {
                            bgColor = 'bg-blue-50 dark:bg-blue-900/30';
                          }
                          
                          return (
                            <div
                              key={dayData.day}
                              className={`aspect-square border border-gray-300 dark:border-gray-600 rounded-lg p-2 flex flex-col items-center justify-start ${bgColor}`}
                            >
                              <div className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                                {dayData.day}
                              </div>
                              {dayData.isFinancialReview && (
                                <div className="w-full mb-1">
                                  <div className="bg-blue-500 text-white text-xs px-1 py-0.5 rounded text-center font-medium">
                                    Financial Review
                                  </div>
                                </div>
                              )}
                              {dayData.isStrategyCall && (
                                <div className="w-full mb-1">
                                  <div className="bg-blue-500 text-white text-xs px-1 py-0.5 rounded text-center font-medium">
                                    Strategy Call
                                  </div>
                                </div>
                              )}
                              {dayData.hasBreakthroughCall && (
                                <div className="w-full">
                                  <div className="bg-green-500 text-white text-xs px-1 py-0.5 rounded text-center font-medium">
                                    Breakthrough Call
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProposalAnalysis;

